import type { DocPageContent } from '../types';

/**
 * Per-SDK backend integration factory (SDKs section).
 *
 * One shared block template instantiated per non-Node server-side SDK, so the
 * i18n surface stays bounded: shared prose keys + three per-SDK keys (title,
 * description, lead). Everything technical — the install command, the secret
 * environment variable, and the verify-intent snippet — is VERBATIM code
 * (never translated) lifted straight from each package's README / public API:
 *
 *   - install command  → the D27 `PUBLISHED_PACKAGES` SoT (`@syncanix/shared`),
 *     which itself mirrors each package manifest (pyproject.toml, go.mod,
 *     pom.xml, composer.json, *.gemspec, Cargo.toml, *.csproj). Because every
 *     package is still `planned` (nothing is live on a registry yet), the code
 *     renderer pairs each install block with the SoT-driven "available at
 *     launch" note automatically — so no page links a registry URL that would
 *     404 today.
 *   - secret env var   → the exact variable each SDK's README reads.
 *   - verify snippet   → the framework quick-start each README documents, using
 *     the real public API (function/class/attribute names verified against the
 *     package source). No invented APIs.
 *
 * The v2 (act-as-user) envelope is documented once in `reference/runtime-api`;
 * these pages link to it rather than duplicating the wire format.
 */

export interface SdkGuide {
  /** Slug segment under `sdks/`. */
  readonly id: string;
  /** Verbatim SDK display name (identical across locales). */
  readonly name: string;
  /** Registry the package targets (verbatim — e.g. `PyPI`, `crates.io`). */
  readonly registry: string;
  /** The exact install command — verbatim, mirrors the PUBLISHED_PACKAGES SoT. */
  readonly installCommand: string;
  /** Code-fence language for the install block. */
  readonly installLanguage: string;
  /** The environment variable this SDK's README reads for the signing secret. */
  readonly secretEnvVar: string;
  /** Code-fence language for the verify snippet. */
  readonly verifyLanguage: string;
  /** Verbatim verify-intent quick-start, lifted from the package README. */
  readonly verifySnippet: string;
}

export const SDK_GUIDES: readonly SdkGuide[] = [
  {
    id: 'python',
    name: 'Python',
    registry: 'PyPI',
    installCommand: `pip install syncanix-sdk[flask]  # or [django], [fastapi]`,
    installLanguage: 'bash',
    secretEnvVar: 'SYNCANIX_INTENT_SECRET',
    verifyLanguage: 'python',
    verifySnippet: `import os
from flask import Flask, g
from syncanix_sdk.flask_middleware import syncanix_verify_intent

app = Flask(__name__)

@app.route('/tools/refund', methods=['POST'])
@syncanix_verify_intent(secret=os.environ['SYNCANIX_INTENT_SECRET'])
def refund():
    intent = g.syncanix_intent  # verified IntentPayload
    return {'tenantId': intent.tenantId}`,
  },
  {
    id: 'go',
    name: 'Go',
    registry: 'pkg.go.dev',
    installCommand: `go get github.com/syncanix-ai/syncanix-sdk-go`,
    installLanguage: 'bash',
    secretEnvVar: 'SYNCANIX_INTENT_SECRET',
    verifyLanguage: 'go',
    verifySnippet: `r := gin.Default()
r.Use(syncanix.GinMiddleware(os.Getenv("SYNCANIX_INTENT_SECRET"), nil))

r.POST("/tools/refund", func(c *gin.Context) {
    payload, _ := c.MustGet(syncanix.GinIntentKey).(*syncanix.IntentPayload)
    c.JSON(200, gin.H{"tenantId": payload.TenantID})
})`,
  },
  {
    id: 'jvm',
    name: 'Spring Boot',
    registry: 'Maven Central',
    installCommand: `implementation "ai.syncanix:syncanix-sdk-spring-boot"`,
    installLanguage: 'groovy',
    secretEnvVar: 'SYNCANIX_INTENT_SECRET',
    verifyLanguage: 'java',
    verifySnippet: `@RestController
@RequestMapping("/tools")
public class RefundController {

  @PostMapping("/refund")
  @SyncanixVerifyIntent
  public Map<String, String> refund(HttpServletRequest request) {
    IntentPayload intent =
        (IntentPayload) request.getAttribute(SyncanixIntentFilter.INTENT_ATTRIBUTE);
    return Map.of("tenantId", intent.getTenantId());
  }
}`,
  },
  {
    id: 'php',
    name: 'PHP',
    registry: 'Packagist',
    installCommand: `composer require syncanix/sdk-php`,
    installLanguage: 'bash',
    secretEnvVar: 'SYNCANIX_SECRET',
    verifyLanguage: 'php',
    verifySnippet: `Route::middleware(SyncanixIntentMiddleware::class . ':' . env('SYNCANIX_SECRET'))
    ->group(function () {
        Route::post('/tools/refund', function (Request $request) {
            $intent = $request->attributes->get('syncanix_intent');
            return response()->json(['tenantId' => $intent->tenantId]);
        });
    });`,
  },
  {
    id: 'ruby',
    name: 'Ruby',
    registry: 'RubyGems',
    installCommand: `bundle add syncanix-sdk-rails`,
    installLanguage: 'bash',
    secretEnvVar: 'SYNCANIX_INTENT_SECRET',
    verifyLanguage: 'ruby',
    verifySnippet: `class ToolsController < ApplicationController
  include SyncanixSdk::Rails::VerifyIntentConcern
  syncanix_verify_intent secret: ENV['SYNCANIX_INTENT_SECRET']

  def refund
    render json: { tenantId: @syncanix_intent.tenant_id }
  end
end`,
  },
  {
    id: 'rust',
    name: 'Rust',
    registry: 'crates.io',
    installCommand: `cargo add syncanix-sdk-rust`,
    installLanguage: 'bash',
    secretEnvVar: 'SYNCANIX_INTENT_SECRET',
    verifyLanguage: 'rust',
    verifySnippet: `use std::sync::Arc;
use axum::{Extension, Router, routing::post, Json};
use syncanix_sdk_rust::axum::{SyncanixIntent, SyncanixConfig};

async fn refund_handler(SyncanixIntent(intent): SyncanixIntent) -> Json<serde_json::Value> {
    Json(serde_json::json!({ "tenantId": intent.tenant_id }))
}

let config = Arc::new(SyncanixConfig::new(std::env::var("SYNCANIX_INTENT_SECRET").unwrap()));

let app: Router<()> = Router::new()
    .route("/tools/refund", post(refund_handler))
    .layer(Extension(config));`,
  },
  {
    id: 'dotnet',
    name: 'ASP.NET Core',
    registry: 'NuGet',
    installCommand: `dotnet add package Syncanix.Sdk.AspNetCore`,
    installLanguage: 'bash',
    secretEnvVar: 'SYNCANIX_SECRET',
    verifyLanguage: 'csharp',
    verifySnippet: `var builder = WebApplication.CreateBuilder(args);
builder.Services.AddSyncanixIntent(
    secret: Environment.GetEnvironmentVariable("SYNCANIX_SECRET")!);

var app = builder.Build();
app.UseSyncanixIntentVerification(toolBasePath: "/tools");

app.MapPost("/tools/refund", (HttpContext ctx) =>
{
    var intent = ctx.GetSyncanixIntent()!;
    return Results.Ok(new { intent.TenantId, intent.UserId });
});

app.Run();`,
  },
] as const;

export function sdkGuidePage(guide: SdkGuide): DocPageContent {
  return {
    slug: `sdks/${guide.id}`,
    titleKey: `docs.sdk.${guide.id}.title`,
    descriptionKey: `docs.sdk.${guide.id}.description`,
    blocks: [
      { kind: 'paragraph', textKey: `docs.sdk.${guide.id}.lead` },

      { kind: 'heading', level: 2, id: 'install', textKey: 'docs.sdk.shared.install.heading' },
      { kind: 'paragraph', textKey: 'docs.sdk.shared.install.body' },
      { kind: 'code', language: guide.installLanguage, code: guide.installCommand },

      { kind: 'heading', level: 2, id: 'configure', textKey: 'docs.sdk.shared.configure.heading' },
      { kind: 'paragraph', textKey: 'docs.sdk.shared.configure.body' },
      {
        kind: 'code',
        language: 'bash',
        code: `export ${guide.secretEnvVar}="your-intent-signing-secret"`,
      },

      { kind: 'heading', level: 2, id: 'verify', textKey: 'docs.sdk.shared.verify.heading' },
      { kind: 'paragraph', textKey: 'docs.sdk.shared.verify.body' },
      {
        kind: 'code',
        language: guide.verifyLanguage,
        code: guide.verifySnippet,
        captionKey: 'docs.sdk.shared.verify.caption',
      },
      {
        kind: 'callout',
        tone: 'note',
        titleKey: 'docs.sdk.shared.v2.title',
        textKey: 'docs.sdk.shared.v2.body',
        links: {
          runtime: {
            target: { docSlug: 'reference/runtime-api' },
            labelKey: 'docs.sdk.shared.v2.link',
          },
        },
      },

      { kind: 'heading', level: 2, id: 'next', textKey: 'docs.common.nextSteps' },
      {
        kind: 'cardGrid',
        cards: [
          {
            titleKey: 'docs.sdk.shared.related.runtime.title',
            textKey: 'docs.sdk.shared.related.runtime.text',
            to: { docSlug: 'reference/runtime-api' },
          },
          {
            titleKey: 'docs.sdk.shared.related.keys.title',
            textKey: 'docs.sdk.shared.related.keys.text',
            to: { docSlug: 'reference/key-formats' },
          },
          {
            titleKey: 'docs.sdk.shared.related.overview.title',
            textKey: 'docs.sdk.shared.related.overview.text',
            to: { docSlug: 'sdks' },
          },
        ],
      },
    ],
  };
}
